/**
 * App.jsx — Root application component
 *
 * Orchestrates:
 * 1. Auth gate — shows login form when unauthenticated
 * 2. RBAC provider — wraps all authenticated content
 * 3. Realtime subscriptions — listens for remote DB changes
 * 4. Layout — sidebar + main content area
 * 5. React Router — page routing
 *
 * The overall structure:
 *   <AuthProvider>
 *     <RBACProvider>
 *       if (loading) → loading spinner
 *       if (!authenticated) → <AuthForm />
 *       if (authenticated) → <Sidebar> + <Routes> + <CommandPalette>
 *     </RBACProvider>
 *   </AuthProvider>
 */

import { useState, useCallback, useEffect, useRef, Component } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { initTelemetry, trackPageView, flush as flushTelemetry } from './lib/telemetry';
import { Box, CircularProgress, Typography, Button, Paper } from '@mui/material';
import { useAuth } from './hooks/useAuth';
import { RBACProvider, useRBAC } from './hooks/useRBAC';
import { useSettings } from './hooks/useSettings';
import { useRealtime } from './hooks/useRealtime';
import Sidebar, { DRAWER_WIDTH_EXPANDED, DRAWER_WIDTH_COLLAPSED } from './components/layout/Sidebar.mui';
import BottomTabBar, { TAB_HEIGHT } from './components/layout/BottomTabBar';
import CommandPalette from './components/layout/CommandPalette';
import FeedbackButton from './components/layout/FeedbackButton';
import { useMobile } from './hooks/useMobile';
import AuthForm from './components/auth/AuthForm';

// Pages
import HomePage from './pages/HomePage';
import JobsPage from './pages/JobsPage';
import InquiriesPage from './pages/InquiriesPage';
import PeoplePage from './pages/PeoplePage';
import CalendarPage from './pages/CalendarPage';
import QuotesPage from './pages/QuotesPage';
import InvoicesPage from './pages/InvoicesPage';
import PayrollPage from './pages/PayrollPage';
import LegalPage from './pages/LegalPage';
import SettingsPage from './pages/SettingsPage';
import CalculatorPage from './pages/CalculatorPage';
import OutreachPage from './pages/OutreachPage';
import VoicenotesPage from './pages/VoicenotesPage';
import VoicePlanBoard from './pages/VoicePlanBoard';
import VehiclePage from './pages/VehiclePage';
import LinksPage from './pages/LinksPage';
import ProjectsPage from './pages/ProjectsPage';
import MyAssignmentsDashboard from './components/projects/MyAssignmentsDashboard';
import HowToPage from './pages/HowToPage';
import HowToDetailPage from './pages/HowToDetailPage';
import RotoTubePage from './pages/RotoTubePage';
import CarouselPage from './pages/CarouselPage';
import PublicSharePage from './pages/PublicSharePage';
import OpenHousePage from './pages/OpenHousePage';
import TodosPage from './pages/TodosPage';

/**
 * ErrorBoundary — catches render errors and shows a recovery UI
 * instead of a white screen.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught:', error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
            <Typography variant="h6" color="error" sx={{ mb: 1 }}>Something went wrong</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {this.state.error?.message || 'Unknown error'}
            </Typography>
            <Button variant="contained" size="small" onClick={() => { this.setState({ hasError: false, error: null }); }} sx={{ mr: 1 }}>Try Again</Button>
            <Button variant="outlined" size="small" onClick={() => { window.location.href = '/'; }}>Go Home</Button>
          </Paper>
        </Box>
      );
    }
    return this.props.children;
  }
}

/**
 * Authenticated layout — sidebar + main content + command palette.
 * Also starts Supabase Realtime subscriptions.
 */

/** Route guard — redirects to home if user lacks the required permission */
function RequirePermission({ perm, feature, level, tool, children }) {
  const { hasAction, hasAccess, hasToolAccess } = useRBAC();
  const { data: settings, isPending: settingsPending } = useSettings();
  const { profile } = useAuth();
  // External contributors are restricted to the projects tool
  if (profile?.is_external && tool !== 'projects') {
    return <Navigate to="/projects/me" replace />;
  }
  // The role matrix lives in settings. Checking before it loads falls back to
  // admin-only, which would bounce everyone else (and their deep link) to Home.
  if (settingsPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  // New level-based check
  if (feature && level) {
    if (!hasAccess(feature, level, settings)) return <Navigate to="/" replace />;
    return children;
  }
  // Tool-based check
  if (tool) {
    if (!hasToolAccess(tool, settings)) return <Navigate to="/" replace />;
    return children;
  }
  // Legacy perm-based check (backward compat)
  if (!hasAction(perm, settings)) return <Navigate to="/" replace />;
  return children;
}

function ExternalAwareHome() {
  const { profile } = useAuth();
  if (profile?.is_external) return <Navigate to="/projects/me" replace />;
  return <HomePage />;
}

/** Employee-only route — external contributors are bounced to their projects. */
function EmployeeRoute({ children }) {
  const { profile } = useAuth();
  if (profile?.is_external) return <Navigate to="/projects/me" replace />;
  return children;
}

/**
 * Records a page view on every route change, plus how long the previous screen
 * was open. Renders nothing — it only needs to sit inside the Router.
 */
function PageViewTracker() {
  const location = useLocation();
  const openedAt = useRef(null);
  const prevPath = useRef(null);

  useEffect(() => { initTelemetry(); }, []);

  useEffect(() => {
    const now = Date.now();
    const path = location.pathname;
    // Attribute the dwell time to the screen being left, not the one arriving.
    const dwellMs = prevPath.current && openedAt.current ? now - openedAt.current : null;
    // The tab a screen opened on (sidebar sub-items deep-link via ?tab=); later tab
    // switches are clicks and arrive through the click capture.
    const tab = new URLSearchParams(location.search).get('tab');
    const extra = {
      ...(dwellMs != null && { fromPage: prevPath.current, prevDwellMs: dwellMs }),
      ...(tab && { tab }),
    };
    trackPageView(path, Object.keys(extra).length ? extra : undefined);
    prevPath.current = path;
    openedAt.current = now;
  }, [location.pathname]);

  return null;
}

function AuthenticatedApp() {
  // Start realtime subscriptions (subscribes on mount, cleans up on unmount)
  useRealtime();

  // Flush any queued telemetry when the authenticated shell unmounts (logout).
  useEffect(() => () => flushTelemetry(), []);

  const { isMobile } = useMobile();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const toggleSidebar = useCallback(() => setSidebarExpanded((v) => !v), []);

  return (
    <RBACProvider>
      <PageViewTracker />
      <Box sx={{ display: 'flex', minHeight: '100dvh', overflowX: 'hidden' }}>
        {/* Sidebar — desktop only */}
        {!isMobile && <Sidebar expanded={sidebarExpanded} onToggle={toggleSidebar} />}

        {/* Main content area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: isMobile ? 1.5 : 3,
            pb: isMobile ? `${TAB_HEIGHT + 16}px` : 3,
            height: '100dvh',
            overflow: 'auto',
            transition: 'margin-left 200ms',
          }}
        >
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<ExternalAwareHome />} />
            <Route path="/todos" element={<EmployeeRoute><TodosPage /></EmployeeRoute>} />
            <Route path="/rototube" element={<EmployeeRoute><RotoTubePage /></EmployeeRoute>} />
            <Route path="/carousel" element={<EmployeeRoute><CarouselPage /></EmployeeRoute>} />
            <Route path="/jobs" element={<RequirePermission feature="jobs" level="view_assigned"><JobsPage /></RequirePermission>} />
            <Route path="/jobs/inquiries" element={<RequirePermission feature="jobs" level="view_assigned"><InquiriesPage /></RequirePermission>} />
            <Route path="/people" element={<RequirePermission feature="crm" level="view_no_financials"><PeoplePage /></RequirePermission>} />
            <Route path="/calendar" element={<RequirePermission feature="calendar" level="own_schedule"><CalendarPage /></RequirePermission>} />
            <Route path="/quotes" element={<RequirePermission feature="quotes" level="view_assigned"><QuotesPage /></RequirePermission>} />
            <Route path="/invoices" element={<RequirePermission feature="invoices" level="view_assigned"><InvoicesPage /></RequirePermission>} />
            <Route path="/payroll" element={<RequirePermission feature="payroll" level="own_total"><PayrollPage /></RequirePermission>} />
            <Route path="/legal" element={<RequirePermission feature="legal" level="own_contracts"><LegalPage /></RequirePermission>} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/calculator" element={<RequirePermission feature="calculator" level="own_earnings"><CalculatorPage /></RequirePermission>} />
            <Route path="/outreach" element={<RequirePermission feature="outreach" level="full"><OutreachPage /></RequirePermission>} />
            <Route path="/voicenotes" element={<RequirePermission tool="voicenotes"><VoicenotesPage /></RequirePermission>} />
            <Route path="/voicenotes/plan/:id" element={<RequirePermission tool="voicenotes"><VoicePlanBoard /></RequirePermission>} />
            <Route path="/vehicle" element={<RequirePermission tool="vehicle"><VehiclePage /></RequirePermission>} />
            <Route path="/links" element={<RequirePermission tool="links"><LinksPage /></RequirePermission>} />
            <Route path="/openhouse" element={<RequirePermission tool="openhouse"><OpenHousePage /></RequirePermission>} />
            <Route path="/projects" element={<RequirePermission tool="projects"><ProjectsPage /></RequirePermission>} />
            <Route path="/projects/me" element={<RequirePermission tool="projects"><MyAssignmentsDashboard /></RequirePermission>} />
            <Route path="/howto" element={<RequirePermission feature="wiki" level="view"><HowToPage /></RequirePermission>} />
            <Route path="/howto/:id" element={<RequirePermission feature="wiki" level="view"><HowToDetailPage /></RequirePermission>} />
            <Route path="/wiki" element={<Navigate to="/howto" replace />} />

            {/* Catch-all: redirect to home */}
            <Route path="*" element={<ExternalAwareHome />} />
          </Routes>
          </ErrorBoundary>
        </Box>

        {/* Mobile bottom tab bar */}
        {isMobile && <BottomTabBar />}

        {/* Command Palette (Cmd+K) — desktop only */}
        {!isMobile && <CommandPalette />}

        {/* Bug / idea box — every signed-in page, desktop and mobile */}
        <FeedbackButton />
      </Box>
    </RBACProvider>
  );
}

/**
 * Loading spinner shown during initial auth check.
 */
function LoadingScreen() {
  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={32} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.disabled">Loading Roto Flight OS...</Typography>
      </Box>
    </Box>
  );
}

/**
 * Root App — gates on auth state.
 */
export default function App() {
  const location = useLocation();
  const { loading, isAuthenticated } = useAuth();

  // Public client-share gallery bypasses the auth gate entirely (anyone with the
  // link, no login). Rendered before the auth check; the authenticated app is
  // untouched so its top-level <Routes> keep matching absolute paths.
  if (location.pathname.startsWith('/m/')) {
    return (
      <Routes>
        <Route path="/m/:token" element={<PublicSharePage />} />
      </Routes>
    );
  }

  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <AuthForm />;
  return <AuthenticatedApp />;
}
